import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Eye, EyeOff, Shield, KeyRound, Lock, AlertTriangle, ArrowLeft, Server, Cpu, Building2, UserPlus, LogIn } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminHeader from "@/components/AdminHeader";

// Security validation schemas
const emailSchema = z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters");
const passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(72, "Password must be less than 72 characters");
const companyIdSchema = z.string().regex(/^MCG-[A-Z0-9]{4}-[A-Z0-9]{4}$/, "Invalid Company ID format (e.g., MCG-A1B2-C3D4)");

const AdminAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const portal = searchParams.get("portal");
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  
  // Sign In States
  const [companyId, setCompanyId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Sign Up States (Platform Control only)
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [signupPortal, setSignupPortal] = useState<string>(
    ["creator_management", "venue_oversight", "platform_control"].includes(portal ?? "")
      ? (portal as string)
      : "creator_management",
  );
  
  // Password Reset
  const [resetEmail, setResetEmail] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  // 2FA
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [showTwoFactor, setShowTwoFactor] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        // Check if user has admin role
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .in("role", ["admin", "platform_control", "creator_management", "venue_oversight"]);

        if (roleData && roleData.length > 0) {
          redirectBasedOnRoles(roleData.map((r) => r.role as string));
        }
      }
    };
    checkSession();
  }, []);

  const portalConfig: Record<string, { path: string; roles: string[]; label: string }> = {
    creator_management: {
      path: "/admin/creator-management",
      roles: ["admin", "platform_control", "creator_management"],
      label: "Creator Management",
    },
    venue_oversight: {
      path: "/admin/venue-oversight",
      roles: ["admin", "platform_control", "venue_oversight"],
      label: "Venue Oversight",
    },
    platform_control: {
      path: "/admin/platform-control",
      roles: ["admin", "platform_control"],
      label: "Platform Control",
    },
  };

  const redirectBasedOnRoles = (roles: string[]) => {
    // If the user arrived from a specific portal card, honour it when permitted
    const requested = portal ? portalConfig[portal] : undefined;
    if (requested) {
      if (roles.some((r) => requested.roles.includes(r))) {
        navigate(requested.path);
        return;
      }
      toast({
        variant: "destructive",
        title: "No access to this portal",
        description: `Your account doesn't have ${requested.label} access. Redirecting to your portal.`,
      });
    }

    if (roles.includes("admin") || roles.includes("platform_control")) {
      navigate("/admin/platform-control");
    } else if (roles.includes("creator_management")) {
      navigate("/admin/creator-management");
    } else if (roles.includes("venue_oversight")) {
      navigate("/admin/venue-oversight");
    } else {
      navigate("/landing-page-2");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();

    // Validate inputs
    try {
      emailSchema.parse(cleanEmail);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: error.errors[0].message,
        });
        setLoading(false);
        return;
      }
    }

    // If company ID is provided, validate it and check via server-side Edge Function
    if (companyId) {
      try {
        companyIdSchema.parse(companyId.toUpperCase());
      } catch (error) {
        if (error instanceof z.ZodError) {
          toast({
            variant: "destructive",
            title: "Invalid Company ID",
            description: error.errors[0].message,
          });
          setLoading(false);
          return;
        }
      }

      // Server-side verification via Edge Function (prevents direct database queries)
      const { data: verifyResult, error: verifyError } = await supabase.functions.invoke(
        'verify-company-login',
        {
          body: {
            company_id: companyId.toUpperCase(),
            email: cleanEmail
          }
        }
      );

      if (verifyError) {
        console.error('[AdminAuth] Verification error:', verifyError);
        toast({
          variant: "destructive",
          title: "Verification Failed",
          description: "Unable to verify company credentials. Please try again.",
        });
        setLoading(false);
        return;
      }

      if (!verifyResult?.valid) {
        let errorTitle = "Verification Failed";
        let errorDescription = verifyResult?.message || "Company verification failed.";
        
        switch (verifyResult?.reason) {
          case 'not_found':
            errorTitle = "Invalid Company ID";
            errorDescription = "This Company ID is not registered.";
            break;
          case 'not_approved':
            errorTitle = "Account Not Approved";
            errorDescription = "Your company account is pending approval. Please wait for the confirmation email.";
            break;
          case 'email_mismatch':
            errorTitle = "Email Mismatch";
            errorDescription = "The email address does not match the registered company.";
            break;
          case 'invalid_format':
            errorTitle = "Invalid Company ID";
            errorDescription = "The Company ID format is invalid.";
            break;
        }
        
        toast({
          variant: "destructive",
          title: errorTitle,
          description: errorDescription,
        });
        setLoading(false);
        return;
      }
    }

    // Proceed with sign in
    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error.message,
      });
      setLoading(false);
      return;
    }

    if (data.user) {
      // Check if user has admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.user.id)
        .in("role", ["admin", "platform_control", "creator_management", "venue_oversight"]);

      if (!roleData || roleData.length === 0) {
        await supabase.auth.signOut();
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You don't have permission to access admin features.",
        });
        setLoading(false);
        return;
      }

      toast({
        title: "Welcome back!",
        description: "You have successfully signed in to the admin portal.",
      });

      redirectBasedOnRoles(roleData.map((r) => r.role as string));
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const cleanEmail = signupEmail.trim().toLowerCase();

    // Validate inputs
    try {
      emailSchema.parse(cleanEmail);
      passwordSchema.parse(signupPassword);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: error.errors[0].message,
        });
        setLoading(false);
        return;
      }
    }

    if (signupPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords Don't Match",
        description: "Please ensure both passwords match.",
      });
      setLoading(false);
      return;
    }

    // Create the Platform Control account
    const { data, error } = await supabase.auth.signUp({
      email: cleanEmail,
      password: signupPassword,
      options: {
        emailRedirectTo: `${window.location.origin}/admin-auth`,
        data: {
          full_name: fullName,
          user_type: signupPortal,
          requested_portal: signupPortal,
        },
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message,
      });
      setLoading(false);
      return;
    }

    if (data.user) {
      toast({
        title: "Account Created!",
        description: "Your agency account has been created. Confirm your email, then sign in — your portal ID appears in the dashboard header once a platform admin activates your portal access.",
      });
      
      // Clear form
      setSignupEmail("");
      setSignupPassword("");
      setConfirmPassword("");
      setFullName("");
      setActiveTab("signin");
    }

    setLoading(false);
  };

  const handlePasswordReset = async () => {
    const cleanEmail = resetEmail.trim().toLowerCase();
    
    try {
      emailSchema.parse(cleanEmail);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Invalid Email",
          description: error.errors[0].message,
        });
        return;
      }
    }

    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Reset Failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for the password reset link.",
      });
      setResetDialogOpen(false);
      setResetEmail("");
    }
    setLoading(false);
  };

  const handleTwoFactorSubmit = async () => {
    if (twoFactorCode.length !== 6) {
      toast({
        variant: "destructive",
        title: "Invalid Code",
        description: "Please enter a valid 6-digit code.",
      });
      return;
    }
    
    setLoading(true);
    setTimeout(() => {
      toast({
        title: "2FA Verified",
        description: "Proceeding to admin dashboard...",
      });
      setTimeout(() => navigate("/admin/platform-control"), 1000);
      setLoading(false);
    }, 1500);
  };

  return (
    <>
      <AdminHeader />
      <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col items-center justify-center pt-24 pb-12 px-4">
        {/* Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsla(0,0%,100%,0.02)_1px,transparent_1px),linear-gradient(to_bottom,hsla(0,0%,100%,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_110%)]"></div>

        {/* Main Auth Container */}
        <div className="relative z-10 w-full max-w-lg mt-8">
          {/* Brand Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-black border border-gray-800">
              <Shield className="w-5 h-5 text-white" />
              <span className="text-sm font-medium">High-Security Access Required</span>
            </div>
          </div>

          {/* Auth Form */}
          <div className="w-full p-8 bg-black/80 backdrop-blur-xl rounded-2xl border border-gray-800 shadow-2xl">
            {!showTwoFactor ? (
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "signin" | "signup")} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-black border border-gray-800 p-1 rounded-xl">
                  <TabsTrigger value="signin" className="data-[state=active]:bg-white data-[state=active]:text-black flex items-center gap-2 rounded-lg transition-all">
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger value="signup" className="data-[state=active]:bg-white data-[state=active]:text-black flex items-center gap-2 rounded-lg transition-all">
                    <UserPlus className="w-4 h-4" />
                    Sign Up
                  </TabsTrigger>
                </TabsList>

              {/* Sign In Tab */}
              <TabsContent value="signin">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-3 p-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 mb-4">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Agency Sign In</h2>
                  <p className="text-gray-400 text-sm">Enter your Company ID and credentials</p>
                </div>

                <form onSubmit={handleSignIn} className="space-y-5">
                  {/* Company ID Field */}
                  <div className="space-y-2">
                    <Label htmlFor="company-id" className="text-gray-300 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Company ID
                      <span className="text-xs text-gray-500">(for Creator Management / Venue Oversight)</span>
                    </Label>
                    <Input
                      id="company-id"
                      type="text"
                      placeholder="MCG-XXXX-XXXX"
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value.toUpperCase())}
                      className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12 font-mono tracking-wider"
                    />
                    <p className="text-xs text-gray-500">Leave blank for Platform Control access</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin-email" className="text-gray-300">Email Address</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      placeholder="admin@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="admin-password" className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="admin-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12 pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Security Notice */}
                  <div className="p-4 rounded-lg bg-black border border-gray-800">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-300">Security Notice</p>
                        <p className="text-xs text-gray-400">
                          This portal is for approved agencies only. All access is logged and monitored.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold h-12 text-base"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        Verifying Credentials...
                      </span>
                    ) : (
                      "Sign In to Admin Portal"
                    )}
                  </Button>

                  <div className="flex items-center justify-between">
                    <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          className="text-sm text-gray-400 hover:text-white hover:bg-gray-800"
                        >
                          <KeyRound className="w-4 h-4 mr-2" />
                          Forgot Password?
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-black border border-gray-800 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-white">Reset Admin Password</DialogTitle>
                          <DialogDescription className="text-gray-400">
                            Enter your registered email address to receive a password reset link.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="reset-email" className="text-gray-300">Email Address</Label>
                            <Input
                              id="reset-email"
                              type="email"
                              placeholder="admin@company.com"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              autoComplete="email"
                              className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20"
                            />
                          </div>
                          <Button 
                            onClick={handlePasswordReset} 
                            className="w-full bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold"
                            disabled={loading}
                          >
                            {loading ? "Sending..." : "Send Reset Link"}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button
                      type="button"
                      variant="outline"
                      className="border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
                      onClick={() => setShowTwoFactor(true)}
                    >
                      2FA Required?
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* Sign Up Tab (Platform Control Only) */}
              <TabsContent value="signup">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-3 p-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 mb-4">
                    <UserPlus className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Agency Portal Sign Up</h2>
                  <p className="text-gray-400 text-sm">Register your agency and get a portal ID for creator and venue management</p>
                </div>

                <form onSubmit={handleSignUp} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Portal</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {([
                        ["creator_management", "Creator Management"],
                        ["venue_oversight", "Venue Oversight"],
                        ["platform_control", "Platform Control"],
                      ] as const).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setSignupPortal(value)}
                          className={`rounded-lg border px-3 py-3 text-xs font-semibold transition-colors ${
                            signupPortal === value
                              ? "border-white bg-white text-black"
                              : "border-gray-800 bg-black text-gray-300 hover:border-gray-600"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      A unique portal ID (MCG-XXXX-XXXX) is issued to your account and shown in your dashboard header.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-name" className="text-gray-300">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-gray-300">Email Address</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="admin@metrocitygroove.com"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showSignupPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12 pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignupPassword(!showSignupPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      >
                        {showSignupPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Minimum 8 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-gray-300">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 h-12"
                    />
                  </div>

                  {/* Info Notice */}
                  <div className="p-4 rounded-lg bg-black border border-gray-800">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-300">Platform Control Access</p>
                        <p className="text-xs text-gray-400">
                          After registration, a super admin must assign your role before you can access the dashboard.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold h-12 text-base"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        Creating Account...
                      </span>
                    ) : (
                      "Create Platform Control Account"
                    )}
                  </Button>

                  <p className="text-center text-sm text-gray-400">
                    For Creator Management or Venue Oversight access,{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/agency-pricing")}
                      className="text-white underline hover:no-underline"
                    >
                      submit a consultation request
                    </button>
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 p-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 mb-4">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Two-Factor Authentication</h3>
                <p className="text-gray-400 mb-4">Enter the 6-digit code from your authenticator app</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="two-factor-code" className="text-gray-300">Authentication Code</Label>
                  <Input
                    id="two-factor-code"
                    type="text"
                    placeholder="000000"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="bg-black border-gray-800 text-white placeholder:text-gray-600 focus:border-gray-500 focus:ring-gray-500/20 text-center text-2xl h-14 tracking-widest"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-gray-700 text-gray-400 hover:text-white hover:border-gray-500"
                    onClick={() => setShowTwoFactor(false)}
                  >
                    Back to Login
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold"
                    onClick={handleTwoFactorSubmit}
                    disabled={loading || twoFactorCode.length !== 6}
                  >
                    {loading ? "Verifying..." : "Verify & Continue"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-800">
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full text-gray-400 hover:text-white hover:bg-gray-800"
              onClick={() => navigate("/landing-page-2")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Home
            </Button>
            <div className="text-center mt-4">
              <p className="text-xs text-gray-500">
                <span className="text-gray-400">© {new Date().getFullYear()} Metro City Groove</span>
                <span className="mx-2">•</span>
                <span>All access is monitored and recorded</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">v1.0.0 • Security Level: Maximum</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminAuth;